import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { Handle, Position } from '@xyflow/react';
import TemplatePreview from './TemplatePreview';
import { Edit, Reply, MessageCircle, MessageSquare, Loader2 } from 'lucide-react';
import { OutlineButton, PrimaryButton } from '../../../components/Buttons';
import axiosInstance from '../../../services/api/axios';
import UseReplyModal from './UseReplyModal';

const InitialMessageNode = React.memo(({ data, id, selected, isConnectable }) => {
  const { label, headerType, headerText, body, buttons = [], onCreateTemplate, onAddReplyNode, isViewOnly, project } = data;
  

  const isTemplateCreated = useMemo(() => Boolean(data?.templateName), [data?.templateName]);
  const [imageUrl, setImageUrl] = useState(null);
  const [objectUrl, setObjectUrl] = useState(null);
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [selectedReplyButtonIndex, setSelectedReplyButtonIndex] = useState(null);
  const [previousTemplates, setPreviousTemplates] = useState([]);
  const [isLoadingWorkflows, setIsLoadingWorkflows] = useState(false);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
  const publishedRepliesCache = useRef({});
 

  // Get current lead type from localStorage
  const getCurrentLeadType = useCallback(() => {
    return localStorage.getItem('current_leadType') || 'standard';
  }, []);

  const shouldShowButtons = useCallback(() => {
    const leadType = getCurrentLeadType();
    return !['birthday_template', 'festival_template', ].includes(leadType);
  }, [getCurrentLeadType]);

  // Memoize the image URL to prevent unnecessary re-renders
  const memoizedImageUrl = useMemo(() => {
    // Handle both headerMedia (local storage) and media (backend) structures
    const mediaData = data.headerMedia || data.media;
    
    if (data.headerType === 'image' && mediaData) {
      if (mediaData.content) {
        return `data:${mediaData.content_type};base64,${mediaData.content}`;
      }
      if (mediaData.base64) {
        return mediaData.base64;
      }
    }
    return null;
  }, [data.headerType, data.headerMedia, data.media]);

  useEffect(() => {
    let url;

    const setupImageUrl = async () => {
      try {
        // Use memoized base64 URL if available
        if (memoizedImageUrl) {
          setImageUrl(memoizedImageUrl);
          return;
        }

        // Handle file-based media - check both headerMedia and media structures
        const mediaData = data.headerMedia || data.media;
        if (data.headerType !== 'text' && mediaData?.file) {
          // Only create new object URL if we don't have one
          if (!objectUrl) {
            url = URL.createObjectURL(mediaData.file);
            setObjectUrl(url);
            setImageUrl(url);
          }
        } else {
          setImageUrl(null);
        }
      } catch (error) {
        console.error('Error setting up image URL:', error);
      }
    };

    setupImageUrl();

    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
        setObjectUrl(null);
      }
    };
  }, [data.headerMedia?.file, data.media?.file, data.headerType, memoizedImageUrl, objectUrl]);

  const loadTemplate = async () => {
    if (isLoadingTemplate) return; // Prevent multiple simultaneous calls
    
    setIsLoadingTemplate(true);
    try {
      let templateData = null;
      const currentLeadType = getCurrentLeadType();
      
      // If this is a saved template (has customerWhatsappTemplateId), fetch from server directly
      const templateId = data?.customerWhatsappTemplateId;
      if (templateId && Number.isInteger(Number(templateId))) {
        // Check if we already have the template data cached
        const cacheKey = `template_${templateId}`;
        const cachedTemplate = sessionStorage.getItem(cacheKey);
        
        if (cachedTemplate) {
          try {
            templateData = JSON.parse(cachedTemplate);
            
            data.onCreateTemplate?.(id, templateData);
            return;
          } catch (error) {
            console.error("Error parsing cached template:", error);
            sessionStorage.removeItem(cacheKey);
          }
        }
        
        try {
          const response = await axiosInstance.get('/customer/whatsapp/template', {
            params: {
              customerWhatsappTemplateId: templateId
            }
          });
        
          // Handle both array and single object responses
          const backendTemplate = Array.isArray(response) ? response[0] : response;
     
          
          if (!backendTemplate) {
            console.error("No template data received from server");
            return;
          }

          // Format the template data to match the expected structure
          templateData = {
            templateName: backendTemplate.friendlyName || backendTemplate.templateName || '',
            headerType: backendTemplate.components?.find(c => c.type === 'HEADER')?.format?.toLowerCase() || 'text',
            headerText: backendTemplate.components?.find(c => c.type === 'HEADER')?.text || '',
            headerMedia: backendTemplate.media ? {
              content: backendTemplate.media.content,
              content_type: backendTemplate.media.content_type,
              key: backendTemplate.media.key
            } : null,
            body: backendTemplate.components?.find(c => c.type === 'BODY')?.text || '',
            buttons: backendTemplate.components?.find(c => c.type === 'BUTTONS')?.buttons?.map(button => ({
              buttonText: button.text || '',
              type: button.type?.toLowerCase() || 'quick_reply',
              typeOfAction: button.type?.toLowerCase() || 'quick_reply',
              label: button.text || '',
              phoneNumber: button.phone_number || '',
              websiteURL: button.url || '',
              configId: button.configId || null
            })) || [],
            leadType: Array.isArray(backendTemplate.persona) ? backendTemplate.persona : [],
            conditions: backendTemplate.conditions || '',
            conditionString: backendTemplate.conditions || '',
            whatsappTemplateStatus: backendTemplate.whatsappTemplateStatus || 'NOT_PUBLISHED',
            customerWhatsappTemplateId: backendTemplate.customerWhatsappTemplateId,
            templateType: backendTemplate.templateType,
            sendDatetime: backendTemplate.sendDatetime,
            isPublished: backendTemplate.isPublished,
            isNotificationSent: backendTemplate.isNotificationSent,
            order: backendTemplate.order,
            customer: backendTemplate.customer,
            project: backendTemplate.project
          };
          
          // // Cache the template data
          // sessionStorage.setItem(cacheKey, JSON.stringify(templateData));
       
          data.onCreateTemplate?.(id, templateData);
          return;
        } catch (error) {
          console.error("Error fetching template from server:", error);
        }
      }

      // Only check localStorage for unsaved templates
      const storedTemplate = localStorage.getItem(`template-${currentLeadType}-${id}`);
      if (storedTemplate) {
        try {
          templateData = JSON.parse(storedTemplate);
    
          data.onCreateTemplate?.(id, templateData);
          return;
        } catch (error) {
          console.error("Error parsing stored template:", error);
        }
      }

      // If no templateId and no stored template, create a new empty template
      templateData = {
        templateName: '',
        headerType: 'text',
        headerText: '',
        headerMedia: null,
        body: '',
        buttons: [],
        leadType: [],
        conditions: '',
        conditionString: '',
        whatsappTemplateStatus: 'NOT_PUBLISHED',
        templateType: 'STANDARD_TEMPLATE',
        isPublished: false,
        isNotificationSent: false,
        order: 0
      };
     
      data.onCreateTemplate?.(id, templateData);
    } finally {
      setIsLoadingTemplate(false);
    }
  };

  const handleButtonClick = useCallback(async () => {
    loadTemplate();
  }, [id, data, getCurrentLeadType, isLoadingTemplate]);

  const handleAddReply = useCallback((buttonIndex) => {
    if (typeof data.onAddReplyNode === 'function') {
      data.onAddReplyNode(id, buttonIndex);
    }
  }, [id, data.onAddReplyNode]);

  // Memoize the template preview to prevent re-renders during node movement
  const templatePreview = useMemo(() => {
    // Handle both headerMedia (local storage) and media (backend) structures
    const mediaData = data.headerMedia || data.media;
    
    return (
      <TemplatePreview
        key={`preview-${id}`}
        headerType={data.headerType}
        headerText={data.headerText}
        headerMedia={mediaData}
        variable={data.variable}
        body={data.body}
        buttons={data.buttons}
        imageUrl={imageUrl}
      />
    );
  }, [data.headerType, data.headerText, data.headerMedia, data.media, data.variable, data.body, data.buttons, id, imageUrl]);

  // Style for handles
  const handleStyle = {
    background: '#F43F5E', // flamingo-600
    width: '12px', 
    height: '12px',
    border: '2px solid white',
    borderRadius: '50%',
    cursor: 'crosshair',
    zIndex: 1000,
    boxShadow: '0 0 4px rgba(0, 0, 0, 0.3)'
  };

  // Style for button handles
  const buttonHandleStyle = {
    background: '#FB7185', // flamingo-400
    width: '10px', 
    height: '10px',
    border: '1.5px solid white',
    borderRadius: '50%',
    cursor: 'crosshair',
    zIndex: 1000,
    boxShadow: '0 0 4px rgba(0, 0, 0, 0.3)'
  };




  useEffect(() => {
    if (showReplyModal && project?.customerId && project?.projectId) {
      const cacheKey = `${project.customerId}-${project.projectId}`;
      if (publishedRepliesCache.current[cacheKey]) {
        setPreviousTemplates(publishedRepliesCache.current[cacheKey]);
      } else {
        setIsLoadingWorkflows(true);
        (async () => {
          try {
            const response = await axiosInstance.get('/customer/whatsapp/drip-campaign', {
              params: {
                customerId: project.customerId,
                projectId: project.projectId
              }
            });

            console.log('Full API Response:', response);
            
         

            const publishedReplies = (response.templates || [])
              .filter(template => {
                const isReplyTemplate = template.templateType === 'REPLY_NODE_TEMPLATE';
               
                
              

                return isReplyTemplate ;
              });

        

            publishedRepliesCache.current[cacheKey] = publishedReplies;
            setPreviousTemplates(publishedReplies);
          } catch (err) {
            console.error('Error fetching published replies:', err);
            setPreviousTemplates([]);
          } finally {
            setIsLoadingWorkflows(false);
          }
        })();
      }
    }
  }, [showReplyModal, project]);

  const handleReplyOptionClick = (index) => {
    setSelectedReplyButtonIndex(index);
    setShowReplyModal(true);
  };

  const handleCreateNewReply = () => {
    setShowReplyModal(false);
    if (typeof data.onAddReplyNode === 'function') {
      data.onAddReplyNode(id, selectedReplyButtonIndex);
    }
  };

  const handleUseExistingTemplate = (template) => {
    setShowReplyModal(false);
    
    if (typeof data.onAddReplyNode === 'function') {
      // Add the reply node
      data.onAddReplyNode(id, selectedReplyButtonIndex);
      console.log('template from reply modal ==================', template);
      // Extract template data
      const templateData = {
        templateName: (template.friendlyName || template.templateName) + ' (Copy)',
        headerType: template.components?.find(c => c.type === 'HEADER')?.format?.toLowerCase() || 'text',
        headerText: template.components?.find(c => c.type === 'HEADER')?.text || '',
        headerMedia: template.components?.find(c => c.type === 'HEADER')?.example?.header_handle || null,
        body: template.components?.find(c => c.type === 'BODY')?.text || '',
        buttons: template.components?.find(c => c.type === 'BUTTONS')?.buttons || [],
        leadType: template.persona || [],
        conditions: template.conditions || '',
        conditionString: template.conditions || '',
        whatsappTemplateStatus: 'NOT_PUBLISHED',
        templateType: 'REPLY_NODE_TEMPLATE',
        isPublished: false,
        isNotificationSent: false,
        order: 0,
        customerWhatsappTemplateId: undefined
      };

      // Save the template data to localStorage
      const currentLeadType = localStorage.getItem('current_leadType') || 'standard';
      
      // Use setTimeout to ensure the node is created first
      setTimeout(() => {
        const replyNodes = document.querySelectorAll('[data-id^="reply-"]');
        if (replyNodes.length > 0) {
          const lastReplyNode = replyNodes[replyNodes.length - 1];
          const newNodeId = lastReplyNode.getAttribute('data-id');
          
          // Save the template data with the correct node ID
          localStorage.setItem(`template-${currentLeadType}-${newNodeId}`, JSON.stringify(templateData));
          
          // Update the node data
          if (data.onCreateTemplate) {
            data.onCreateTemplate(newNodeId, templateData);
          }
        }
      }, 100);
    }
  };

  return (
    <div 
      className={`bg-white rounded-lg shadow-md border-2 border-flamingo-200 overflow-hidden hover:shadow-lg transition-shadow duration-200`}
      style={{ minWidth: '280px', maxWidth: '320px' }}
    >
      {/* Standard handles */}
      <Handle
        type="source"
        position={Position.Right}
        id="right"
        style={{
          ...handleStyle,
          background: '#FB7185', // flamingo-400
          border: '2px solid white',
          boxShadow: '0 0 4px rgba(251, 113, 133, 0.3)' // lighter flamingo shadow
        }}
        isConnectable={!isViewOnly && isConnectable}
      />
      
      <Handle
        type="target"
        position={Position.Left}
        id="left"
        style={{
          ...handleStyle,
          background: '#FB7185', // flamingo-400
          border: '2px solid white',
          boxShadow: '0 0 4px rgba(251, 113, 133, 0.3)' // lighter flamingo shadow
        }}
        isConnectable={!isViewOnly && isConnectable}
      />

      {/* Node Header */}
      <div className=" border-b-2 bg-flamingo-500 text-white py-2 px-3 font-medium flex items-center justify-between shadow-sm">
        <div className="flex items-center">
          <MessageSquare className="mr-2 flex-shrink-0" />
          <span className="text-sm truncate max-w-[230px]">{label || 'Initial Message'}</span>
        </div>
      </div>

      {/* Template Preview with border */}
      <div className="px-3 pt-3">
        <div className="border border-gray-200 rounded-md overflow-hidden bg-gray-50">
          {templatePreview}
        </div>
      </div>

      {/* Action Buttons & Reply Options */}
      <div className="p-3 bg-gray-50 border-t border-gray-200 mt-3">
        {/* Show edit button for both unpublished and published templates */}
        {(!isViewOnly || data.isPublished) && (
          <OutlineButton
            onClick={handleButtonClick}
            className='w-full'
            disabled={isLoadingTemplate}
          >
            {isLoadingTemplate ? (
              <>
                <Loader2 size={16} className="mr-2 animate-spin" />
                Loading...
              </>
            ) : (
              <>
                <Edit size={16} className="mr-2" />
                {console.log('data.isPublished:', data.isPublished)}
                {data.isPublished ? 'Edit Published Template' : 'Edit Template'}
              </>
            )}
          </OutlineButton>
        )}
        {/* Reply Options with Handles (hide in view mode) */}
        {(() => {
       
            
            // Modified condition to show reply options - hide in view mode
            const hasQuickReplyButtons = data.buttons?.some(button => 
                button.type === 'quick_reply' || 
                button.typeOfAction === 'quick_reply' ||
                button.type === 'QUICK_REPLY'
            );

            return (isTemplateCreated || data.customerWhatsappTemplateId) && 
                   shouldShowButtons() && 
                   hasQuickReplyButtons && (
                <div className="mt-3 flex flex-col gap-2">
                    {isViewOnly ? (
                      <p className="text-xs font-medium text-gray-400 mb-1">
                        BUTTONS
                      </p>
                    ) : (
                      <p className="text-xs font-medium text-gray-600 mb-1">REPLY OPTIONS:</p>
                    )}
                    <div className="space-y-2">
                        {data.buttons
                        
                            .filter(button => 
                                button.type === 'quick_reply' || 
                                button.typeOfAction === 'quick_reply' ||
                                button.type === 'QUICK_REPLY'
                                
                            )
                            
                            .map((button, index) => (
                            
                                <div key={index} className="relative flex items-center">
                                  {
                                    console.log('Reply Option Button:', button)
                                  }
                                    <div
                                        className={`w-full py-1.5 px-3 text-sm border border-gray-300 rounded-md bg-white ${
                                            isViewOnly || data.isPublished ? 'cursor-default' : 'hover:bg-gray-50 hover:border-gray-400 cursor-pointer'
                                        } text-gray-700 font-medium flex items-center justify-between transition-colors shadow-sm`}
                                        onClick={!isViewOnly && !data.isPublished ? () => handleReplyOptionClick(index) : undefined}
                                    >
                                        <span className="truncate max-w-[200px]">{button.buttonText || button.text || `Button ${index + 1}`}</span>
                                        <Reply size={14} className="text-gray-500 flex-shrink-0" />
                                    </div>
                                    
                                        <Handle
                                            type="source"
                                            position={Position.Right}
                                            id={`button-${index}`}
                                            style={{
                                                ...buttonHandleStyle,
                                                left: '100%',
                                                marginLeft: 4,
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                position: 'absolute'
                                            }}
                                            isConnectable={!isViewOnly && isConnectable}
                                        />
                                    
                                </div>
                            ))}
                    </div>
                </div>
            );
        })()}
      </div>

      {/* Replace the Modal with UseReplyModal */}
      <UseReplyModal
        open={showReplyModal}
        onClose={() => setShowReplyModal(false)}
        onCreateNewReply={handleCreateNewReply}
        onUseExistingTemplate={handleUseExistingTemplate}
        previousTemplates={previousTemplates}
        isLoadingWorkflows={isLoadingWorkflows}
      />
    </div>
  );
});

InitialMessageNode.displayName = 'InitialMessageNode';

export default InitialMessageNode;